import React, { Component } from 'react';
import './Pantheon.css';
import Nav from '../../nav/Nav';
import Back from '../../Back';
import Img from '../../../StableImage';
import { Navigate } from "react-router-dom";

import Library from '../../../scene/utility/Library';
import User from '../../../User';
import { read } from '../../../TextManager';

const PANTHEON_ORDER = ["nilith", "nikara", "aurora", "amaryllis", "zephyrus"];

const devotionSelections = () => Object.fromEntries(
  Object.entries(User.data.devotion).map(([key, obj]) => [key, obj.selected])
);

const devotionRanks = () => Object.fromEntries(
  Object.entries(User.data.devotion).map(([key, obj]) => [key, obj.rank])
);

export default class Pantheon extends Component {

  state = {
    index: 0,
    values: devotionSelections(),
    maxValues: devotionRanks()
  };

  get celestials () {

    return PANTHEON_ORDER
      .filter(key => User.hasMetCelestial(key))
      .map(key => Library.getSpirit(key))
      .filter(spirit => spirit);
  }

  devotionValue (property, rank, token="X") {

    switch (property) {
    case "exp":
      return (rank - 1) * 20;
    case "passive":
      return rank - 1;
    case "cards":
      return rank;
    case "health":
      return token === "Y" ? (rank + 1) * 5 : 40 + 20 * rank;
    case "maze":
      return rank;
    default:
      return "";
    }
  }

  devotionEffect (property, rank) {

    const effects = {
      exp: "Get [X]% more exp towards level up.",
      passive: read('traits/passiveupgradeeffect'),
      cards: read('traits/cardrankeffect'),
      health: read('traits/healtheffect'),
      maze: "Start runs at maze power [X]."
    };

    return (effects[property] || "")
      .replace("[X]", this.devotionValue(property, rank, "X"))
      .replace("[Y]", this.devotionValue(property, rank, "Y"));
  }

  cardRankUpdateLimit () {

    return User.champion === 5 ? 5 : 9;
  }

  cardRankUpdateWarning (spirit, rank) {

    const limit = this.cardRankUpdateLimit();
    if (spirit.devotion !== "cards" || rank < limit)
      return null;
    return "Rank " + limit + " is the highest card rank in this update.";
  }

  mazePowerUpdateLimit () {

    return User.champion === 5 ? 1 : 2;
  }

  mazePowerUpdateWarning (spirit, rank) {

    const limit = this.mazePowerUpdateLimit();
    if (spirit.devotion !== "maze" || rank < limit)
      return null;
    return "Rank " + limit + " is the highest maze power in this update.";
  }

  updateWarning (spirit, rank) {

    return this.cardRankUpdateWarning(spirit, rank) || this.mazePowerUpdateWarning(spirit, rank);
  }

  refreshDevotion () {

    this.setState({values: devotionSelections(), maxValues: devotionRanks()});
  }

  selectCelestial (direction) {

    const celestials = this.celestials;
    if (celestials.length === 0)
      return;
    this.setState({index: (this.state.index + direction + celestials.length) % celestials.length});
  }

  selectRank (spirit, direction) {

    const property = spirit.devotion;
    const value = Math.max(1, Math.min(this.state.maxValues[property], this.state.values[property] + direction));
    if (value === this.state.values[property])
      return;
    User.setDevotion(User.difficulty, User.champion, property, value);
    this.refreshDevotion();
  }

  shardSpendAmount (devotion) {

    const remaining = Math.max(0, User.devotionRequirement(devotion.rank) - devotion.progress);
    return Math.min(User.shards, remaining);
  }

  spendShards (spirit) {

    const devotion = User.data.devotion[spirit.devotion];
    const amount = this.shardSpendAmount(devotion);
    if (amount <= 0)
      return;
    User.setShards(User.shards - amount, User.champion, User.difficulty);
    User.addDevotionExp(User.difficulty, User.champion, spirit.devotion, amount);
    this.refreshDevotion();
  }

  renderProgressGauge (spirit, devotion) {

    const requirement = User.devotionRequirement(devotion.rank);
    const progressRatio = Math.max(0, Math.min(1, devotion.progress / requirement));

    return (
      <div className="pantheon-spirit-gauge">
        <div className="pantheon-spirit-rank">
          <Img className="pantheon-spirit-icon" src={"/images/" + spirit.key + ".png"} alt="" />
          <div className="pantheon-spirit-rank-value">{devotion.rank || ""}</div>
        </div>
        <div className="pantheon-spirit-bar">
          <div className="pantheon-spirit-bar-fill" style={{clipPath: "inset(0 " + ((1 - progressRatio) * 100) + "% 0 0)"}}/>
          <div className="pantheon-spirit-bar-label">{devotion.progress + " / " + requirement + " shards"}</div>
        </div>
      </div>
    );
  }

  render () {

    const celestials = this.celestials;
    if (celestials.length === 0)
      return <Navigate to="/" replace />;
    const spirit = celestials[this.state.index];
    const devotion = User.data.devotion[spirit.devotion];
    const shardSpendAmount = this.shardSpendAmount(devotion);
    const selectedRank = this.state.values[spirit.devotion];
    const maxRank = this.state.maxValues[spirit.devotion];
    const updateWarning = this.updateWarning(spirit, maxRank);
    const isNilith = spirit.devotion === "maze";
    const size = isNilith
      ? 30 + (devotion.rank - 1) * 25
      : devotion.rank * 5 + Math.max(0, Math.min(1, devotion.progress / User.devotionRequirement(devotion.rank))) * 5;
    const scale = 0.6 + size * 0.004;
    const translate = Math.max(0, 40 - size * 0.2);

    return (
      <div className={"main-page pantheon-page champion-" + User.champion + " pantheon-" + spirit.key}>
        <div className="main-background pantheon-background">
          <img alt="" src="/images/background.png"/>
        </div>
        <Nav onChangeDifficulty={() => this.refreshDevotion()}/>
        <div className="main pantheon-main">
          <div className="pantheon-stage">
            <div className="pantheon-spirit" style={{transform: "scale(" + scale + ") translateX(" + translate + "vh)"}}>
              <Img
                key={spirit.key}
                priority
                className="pantheon-spirit-img"
                src={spirit.img}
                fallbackSrc={"/images/" + spirit.key + ".png"}
                alt=""
              />
            </div>
            {celestials.length > 1 && <button className="pantheon-switch pantheon-switch-left" onClick={() => this.selectCelestial(-1)}>
              <Img alt="" src="/images/arrowhead.png"/>
            </button>}
            {celestials.length > 1 && <button className="pantheon-switch pantheon-switch-right" onClick={() => this.selectCelestial(1)}>
              <Img alt="" src="/images/arrowhead.png"/>
            </button>}
            <div className="pantheon-info">
              <div className="pantheon-name">{spirit.name}</div>
              {!isNilith && this.renderProgressGauge(spirit, devotion)}
              {!isNilith && <button
                className="pantheon-shard-offer"
                disabled={shardSpendAmount <= 0}
                onClick={() => this.spendShards(spirit)}
              >
                <span>Offer</span>
                <Img src="/images/soulshard.png" alt="" />
                <strong>{shardSpendAmount}</strong>
              </button>}
              <div className="pantheon-blessing-name">{spirit.name + "'s blessing"}</div>
              <div className="pantheon-blessing-description">{spirit.tooltip}</div>
              <div className="pantheon-selected-rank">
                <button className="pantheon-rank-arrow pantheon-rank-arrow-left" onClick={() => this.selectRank(spirit, -1)}>
                  <Img alt="" src="/images/arrowhead.png"/>
                </button>
                <div className="pantheon-rank-value">
                  <span>{read('traits/rank')}</span>
                  <strong>{selectedRank}</strong>
                  <em>{"/ " + maxRank}</em>
                </div>
                <button className="pantheon-rank-arrow pantheon-rank-arrow-right" onClick={() => this.selectRank(spirit, 1)}>
                  <Img alt="" src="/images/arrowhead.png"/>
                </button>
              </div>
              <div className="pantheon-effect">
                {this.devotionEffect(spirit.devotion, selectedRank).split(/(\d+)/g).map((tok, i) =>
                  /^\d+$/.test(tok)
                    ? <span key={i} className="pantheon-blessing-value">{tok}</span>
                    : <React.Fragment key={i}>{tok}</React.Fragment>
                )}
              </div>
              {updateWarning && <div className="pantheon-update-warning">{updateWarning}</div>}
            </div>
          </div>
        </div>
        <Back to="/play"/>
      </div>
    );
  }
}
